import { handleActions, createAction } from "redux-actions";

import { api } from "../../../services";
import {
  MakeModelInfoListItem,
  MakeModelInfoSavePayload
} from "../../../typings/interfaces";

const ADMIN_MAKE_MODEL_INFO_LIST_GET = "ADMIN_MAKE_MODEL_INFO_LIST_GET";
const ADMIN_MAKE_MODEL_INFO_LIST_CLEAR = "ADMIN_MAKE_MODEL_INFO_LIST_CLEAR";
const ADMIN_MAKE_MODEL_INFO_FETCH = "ADMIN_MAKE_MODEL_INFO_FETCH";
const ADMIN_MAKE_MODEL_INFO_FIND_ID = "ADMIN_MAKE_MODEL_INFO_FIND_ID";
const ADMIN_MAKE_MODEL_INFO_SAVE = "ADMIN_MAKE_MODEL_INFO_SAVE";
const ADMIN_MAKE_MODEL_INFO_PUBLISH = "ADMIN_MAKE_MODEL_INFO_PUBLISH";

interface AdminMakeModelInfoState {
  items: MakeModelInfoListItem[];
  page: number;
  itemPerPage: number;
  total: number;
  filters: any;
}

const defaultState: AdminMakeModelInfoState = {
  items: [],
  page: 1,
  itemPerPage: 20,
  total: 0,
  filters: {}
};

// handleResponse in the fetch layer calls response.json() unconditionally on
// 2xx, which rejects with SyntaxError when the BE returns an empty body (204
// No Content or any empty 2xx). Treat that as a successful empty response so
// save/publish don't look like failures when the BE doesn't echo a body.
const treatEmptyBodyAsSuccess = (error: any) => {
  if (error && error.name === "SyntaxError") {
    return null;
  }
  throw error;
};

export const getAdminMakeModelInfoList = createAction(
  ADMIN_MAKE_MODEL_INFO_LIST_GET,
  async (filters: any, page: number, itemPerPage: number, callback?: any) => {
    try {
      const response = await api.MakeModelInfo.list({ ...filters, page, itemPerPage });
      // BE may return a bare array or a paginated wrapper { data, count }.
      const items = Array.isArray(response)
        ? response
        : (response && response.data) || [];
      const total = Array.isArray(response)
        ? response.length
        : (response && (response.count || response.total)) || items.length;
      callback && callback(true);
      return { items, page, itemPerPage, total, filters };
    } catch (error) {
      callback && callback(false, error);
      return { items: [], page, itemPerPage, total: 0, filters };
    }
  }
);

export const clearAdminMakeModelInfoList = createAction(ADMIN_MAKE_MODEL_INFO_LIST_CLEAR);

export const fetchAdminMakeModelInfo = createAction(
  ADMIN_MAKE_MODEL_INFO_FETCH,
  async (id: string, callback?: any) => {
    try {
      const response = await api.MakeModelInfo.getById(id);
      const data = response || null;
      callback && callback(data);
      return data;
    } catch (error) {
      callback && callback(null, error);
      return null;
    }
  }
);

// After a successful save the FE needs the entity id to call the publish
// endpoint. On edit that's already in payload.id, but on create the BE may
// return an empty 204 with no body and no Location header, in which case we
// look our just-saved entry up in the list by (makeId, modelId).
export const findAdminMakeModelInfoIdByMakeModel = createAction(
  ADMIN_MAKE_MODEL_INFO_FIND_ID,
  async (makeId: string, modelId: string, callback?: any) => {
    try {
      const response = await api.MakeModelInfo.list({ makeId, modelId, page: 1, itemPerPage: 20 });
      const items = Array.isArray(response)
        ? response
        : (response && response.data) || [];
      // BE filters by (makeId, modelId) server-side and the tuple is unique
      // per entry, so the response has at most one row. List items don't
      // expose makeId/modelId as fields, so trust the server filter rather
      // than trying to match client-side.
      const id = (items[0] && items[0].id) || null;
      callback && callback(id);
      return id;
    } catch (error) {
      callback && callback(null, error);
      return null;
    }
  }
);

export const saveAdminMakeModelInfo = createAction(
  ADMIN_MAKE_MODEL_INFO_SAVE,
  async (payload: MakeModelInfoSavePayload, callback?: any) => {
    try {
      const response = await api.MakeModelInfo.save(payload, treatEmptyBodyAsSuccess);
      // Distinguish "saved with empty body" from "missing on edit" — on create
      // we genuinely need an id back for the publish step, so return a marker
      // object that consumers can fall back from to the payload.id.
      const saved = response ? response : { ok: true };
      callback && callback(saved);
      return saved;
    } catch (error) {
      callback && callback(null, error);
      return null;
    }
  }
);

export const publishAdminMakeModelInfo = createAction(
  ADMIN_MAKE_MODEL_INFO_PUBLISH,
  async (id: string, callback?: any) => {
    try {
      await api.MakeModelInfo.publish(id, treatEmptyBodyAsSuccess);
      callback && callback(true);
      return true;
    } catch (error) {
      callback && callback(false, error);
      return false;
    }
  }
);

const updateState = (state: AdminMakeModelInfoState, { payload }: any) => ({
  ...state,
  ...payload
});

export default handleActions(
  {
    [ADMIN_MAKE_MODEL_INFO_LIST_GET]: updateState,
    [ADMIN_MAKE_MODEL_INFO_LIST_CLEAR]: () => defaultState
  },
  defaultState
);



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageMakeModelInfoPage/makeModelInfo.ducks.ts